import React, { useEffect, useMemo, useState } from "react";
import { Marker, Popup, Circle } from "react-leaflet";
import L, { Icon } from "leaflet";
import "leaflet/dist/leaflet.css";
import StyledMap from "./styledMap";
import { IMachinesMappedData } from "../../models/parcelat";

// Custom icon for markers
const customIcon = new Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const greenIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

type IMap = {
  values?: any;
  setLockerInfo: React.Dispatch<React.SetStateAction<IMachinesMappedData>>;
  machines: IMachinesMappedData[];
  mapHeight?: string;
};

const AssignLockerOnMap = ({ values, setLockerInfo, machines, mapHeight }: IMap) => {
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Get user's current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.warn("Error getting location:", error);
          // Keep null, will fall back to default coordinates
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    }
  }, []);

  const cordinates = useMemo(() => {
    // Use values coordinates if provided
    if (values?.latitude && values?.longitude) {
      return {
        lat: values.latitude,
        lng: values.longitude,
      };
    }
    
    // Use user's current location if available
    if (userLocation) {
      return userLocation;
    }
    
    // Default to Riyadh coordinates
    return {
      lat: 24.7136, 
      lng: 46.6753, 
    };
  }, [values?.latitude, values?.longitude, userLocation]);

  // Determine zoom level: closer if user location is available
  const zoomLevel = userLocation ? 16 : 12;

  return (
    <StyledMap
      zoomed={zoomLevel}
      centroid={cordinates}
      mapContainerHeight={mapHeight}
      children={
        <>
          {/* Show user's location as a circle if available */}
          {userLocation && (
            <Circle
              center={[userLocation.lat, userLocation.lng]}
              radius={50}
              pathOptions={{ color: 'blue', fillColor: 'blue', fillOpacity: 0.3 }}
            />
          )}
          {machines?.map((point, index) => (
            <Marker
              key={index}
              position={[point.lat ?? 0, point.lng ?? 0]}
              icon={greenIcon}
              eventHandlers={{
                click: () => setLockerInfo(point),
              }}
            >
              <Popup>{point.displayName}</Popup>
            </Marker>
          ))}
        </>
      }
    />
  );
};

export default AssignLockerOnMap;
